import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { UserSettingsContext } from "../context/userSettings";
import {
    Logo,
    NavbarContainer,
    NavButton,
    NavBackdrop,
    FloatingMenuButton,
} from "./NavSettingsStyles";
import { getColor } from "../styles/stylesUtils";
import { getImageUrl } from "../utils/mediaUrl";
import ThemeToggle from "./ThemeToggle";
import SendFeedbackModal from "./SendFeedbackModal";

const EmailText = styled.div`
    font-size: 0.7em;
    color: ${getColor("black", 0.5)};
    text-align: center;
    margin-top: 0.4em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 0.6em;
`;

const checkIfTablet = () => window.innerWidth <= 1024;

const NavSettings = ({
    onLogoClick,
    triggerNotification,
    isNavOpen,
    setIsNavOpen,
    isFeedbackModalOpen,
    setIsFeedbackModalOpen,
    showManageSubscription = false,
    onManageSubscription = null,
    isManagingSubscription = false,
}) => {
    const { isLoggedIn, settings, logout } = useContext(UserSettingsContext);
    const [isTablet, setIsTablet] = useState(checkIfTablet());

    // Initialize nav state on mount based on screen size
    useEffect(() => {
        setIsNavOpen(!checkIfTablet());
    }, [setIsNavOpen]);

    // Update tablet state and nav visibility on resize
    useEffect(() => {
        let resizeTimeout;

        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const nowTablet = checkIfTablet();
                if (nowTablet !== isTablet) {
                    setIsTablet(nowTablet);
                    setIsNavOpen(!nowTablet);
                }
            }, 150);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            clearTimeout(resizeTimeout);
            window.removeEventListener("resize", handleResize);
        };
    }, [isTablet, setIsNavOpen]);

    // Close nav on Escape key (tablet/mobile only)
    useEffect(() => {
        if (!isTablet) return;

        const handleEscape = (e) => {
            if (e.key === "Escape" && isNavOpen) {
                setIsNavOpen(false);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isNavOpen, isTablet, setIsNavOpen]);

    return (
        <>
            {/* Floating Menu Button - tablet/mobile only */}
            <AnimatePresence>
                {isTablet && isLoggedIn && !isNavOpen && (
                    <FloatingMenuButton
                        onClick={() => setIsNavOpen(true)}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                            type: "spring",
                            bounce: 0.4,
                            stiffness: 300,
                            damping: 20,
                            duration: 0.3,
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    />
                )}
            </AnimatePresence>

            {/* Logo */}
            <Logo
                onClick={onLogoClick}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ opacity: { duration: 0.3 } }}
            >
                <img src={getImageUrl("logo.svg")} alt="logo" />
            </Logo>

            {/* Backdrop for tablet/mobile */}
            <AnimatePresence>
                {isNavOpen && isTablet && (
                    <NavBackdrop
                        key="nav-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setIsNavOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Nav */}
            <AnimatePresence>
                {isNavOpen && (
                    <NavbarContainer
                        key="nav-container"
                        initial={{ opacity: 0, y: "5%" }}
                        animate={{ opacity: 1, y: "0%" }}
                        exit={{
                            opacity: 0,
                            y: "0%",
                            transition: {
                                y: {
                                    type: "tween",
                                    duration: 0.3,
                                    ease: "easeIn",
                                },
                                opacity: { duration: 0.2 },
                            },
                        }}
                        transition={{
                            y: {
                                type: "spring",
                                bounce: 1,
                                stiffness: 300,
                                damping: 21,
                                duration: 0.3,
                            },
                            opacity: { duration: 0.2, ease: "easeInOut" },
                        }}
                    >
                        {isLoggedIn && (
                            <>
                                <ThemeToggle />
                                {showManageSubscription && (
                                    <NavButton
                                        className="manage-subscription"
                                        disabled={isManagingSubscription}
                                        onClick={() => {
                                            if (onManageSubscription) {
                                                onManageSubscription();
                                            }
                                        }}
                                    >
                                        <div className="icon" />
                                        {isManagingSubscription
                                            ? "Opening..."
                                            : "Manage subscription"}
                                    </NavButton>
                                )}
                                <NavButton
                                    className="send-feedback"
                                    onClick={() => {
                                        setIsFeedbackModalOpen(true);
                                        if (isTablet) {
                                            setIsNavOpen(false);
                                        }
                                    }}
                                >
                                    <div className="icon" />
                                    Send feedback
                                </NavButton>
                                <NavButton className="logout" onClick={logout}>
                                    Log out
                                </NavButton>
                                {settings?.email && (
                                    <EmailText>{settings.email}</EmailText>
                                )}
                            </>
                        )}
                    </NavbarContainer>
                )}
            </AnimatePresence>

            {/* Feedback Modal */}
            <SendFeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                triggerNotification={triggerNotification}
            />
        </>
    );
};

export default NavSettings;
